import { supabase } from '../supabase';
import { logger } from '../logger';

export class SupabaseService {
  private static instance: SupabaseService;

  private constructor() {}

  static getInstance(): SupabaseService {
    if (!SupabaseService.instance) {
      SupabaseService.instance = new SupabaseService();
    }
    return SupabaseService.instance;
  }

  private formatDateFields(record: any): any {
    const dateFields = ['data', 'data_de_nascimento', 'data_nascimento', 'created_at', 'updated_at'];
    const formattedRecord = { ...record };

    for (const field of dateFields) {
      if (formattedRecord[field]) {
        try {
          let date: Date;
          const value = formattedRecord[field];

          // Verifica se a data está no formato DD/MM/YYYY HH:mm:ss
          if (typeof value === 'string' && value.includes('/')) {
            const [datePart, timePart] = value.split(' ');
            const [day, month, year] = datePart.split('/').map(Number);
            
            if (timePart) {
              const [hours, minutes, seconds] = timePart.split(':').map(Number);
              date = new Date(year, month - 1, day, hours, minutes, seconds);
            } else {
              date = new Date(year, month - 1, day);
            }
          } else {
            date = new Date(value);
          }

          // Verifica se é uma data válida
          if (!isNaN(date.getTime())) {
            // Formatar para timestamp com timezone no formato ISO
            formattedRecord[field] = date.toISOString();
          }
        } catch (error) {
          logger.warn(`Erro ao formatar campo de data ${field}:`, error as Error);
          // Manter o valor original se houver erro
          delete formattedRecord[field];
        }
      }
    }

    return formattedRecord;
  }

  async moveRecord(sourceTable: string, targetTable: string, recordId: string): Promise<void> {
    try {
      // Buscar registro na tabela de origem
      const { data: record, error: fetchError } = await supabase
        .from(sourceTable)
        .select('*')
        .eq('id', recordId)
        .single();

      if (fetchError) {
        logger.error('Erro ao buscar registro:', fetchError);
        throw fetchError;
      }

      if (!record) {
        throw new Error('Registro não encontrado');
      }

      // Formatar campos de data antes de inserir
      const formattedRecord = this.formatDateFields(record);

      // Remover campos que podem causar conflito
      delete formattedRecord.created_at;
      delete formattedRecord.updated_at;

      // Inserir registro na tabela de destino
      const { error: insertError } = await supabase
        .from(targetTable)
        .insert([{ ...formattedRecord, id: recordId }]);

      if (insertError) {
        logger.error('Erro ao inserir registro:', insertError);
        throw insertError;
      }

      // Excluir registro da tabela de origem
      const { error: deleteError } = await supabase
        .from(sourceTable)
        .delete()
        .eq('id', recordId);

      if (deleteError) {
        logger.error('Erro ao excluir registro original:', deleteError);
        throw deleteError;
      }

      logger.info('Registro movido com sucesso', {
        sourceTable,
        targetTable,
        recordId
      });
    } catch (error) {
      logger.error('Erro ao mover registro:', error);
      throw error;
    }
  }

  async deleteRecord(tableName: string, recordId: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', recordId);

      if (error) {
        logger.error('Erro ao excluir registro:', error);
        return { error };
      }

      logger.info('Registro excluído com sucesso', {
        tableName,
        recordId
      });

      return { error: null };
    } catch (error) {
      logger.error('Erro ao excluir registro:', error);
      return { error: error as Error };
    }
  }

  async addErrorToRecord(tableName: string, recordId: string, errorMessage: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ motivo_do_erro: errorMessage })
        .eq('id', recordId);

      if (error) {
        logger.error('Erro ao adicionar mensagem de erro:', error);
        throw error;
      }

      logger.info('Mensagem de erro adicionada com sucesso', {
        tableName,
        recordId,
        errorMessage
      });
    } catch (error) {
      logger.error('Erro ao adicionar mensagem de erro:', error);
      throw error;
    }
  }
}

export const supabaseService = SupabaseService.getInstance();